import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useState,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';

interface FormFieldContextValue {
  descriptionId: string;
  errorId: string;
  hasDescription: boolean;
  hasMessage: boolean;
  id: string;
  invalid: boolean;
  setHasDescription: (value: boolean) => void;
  setHasMessage: (value: boolean) => void;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

function useFormField() {
  const context = useContext(FormFieldContext);
  if (context === null) {
    throw new Error('Form field primitives must be used within FormField.');
  }
  return context;
}

export function Form({ children, ...props }: HTMLAttributes<HTMLFormElement>) {
  return <form {...props}>{children}</form>;
}

export interface FormFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  children: ReactNode;
  id?: string;
  invalid?: boolean;
}

export function FormField({ children, id: providedId, invalid = false, ...props }: FormFieldProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const [hasDescription, setHasDescription] = useState(false);
  const [hasMessage, setHasMessage] = useState(false);
  return (
    <FormFieldContext.Provider value={{ descriptionId: `${id}-description`, errorId: `${id}-error`, hasDescription, hasMessage, id, invalid, setHasDescription, setHasMessage }}>
      <div {...props}>{children}</div>
    </FormFieldContext.Provider>
  );
}

export const FormItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function FormItem(
  { className, ...props },
  ref,
) {
  return <div className={cn('grid gap-1.5', className)} ref={ref} {...props} />;
});

export type FormLabelProps = Omit<LabelHTMLAttributes<HTMLLabelElement>, 'htmlFor'>;

export const FormLabel = forwardRef<HTMLLabelElement, FormLabelProps>(function FormLabel(
  { className, ...props },
  ref,
) {
  const { id, invalid } = useFormField();
  return <label className={cn('text-sm font-medium', invalid && 'text-[var(--foreground)] underline decoration-[var(--destructive)] underline-offset-4', className)} ref={ref} {...props} htmlFor={id} />;
});

export type FormControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-describedby' | 'aria-invalid' | 'id'>;

export const FormControl = forwardRef<HTMLInputElement, FormControlProps>(function FormControl(
  { className, ...props },
  ref,
) {
  const { descriptionId, errorId, hasDescription, hasMessage, id, invalid } = useFormField();
  const describedBy = [hasDescription ? descriptionId : undefined, invalid && hasMessage ? errorId : undefined]
    .filter((value): value is string => value !== undefined)
    .join(' ') || undefined;
  return <input aria-describedby={describedBy} aria-invalid={invalid || undefined} className={cn('flex h-9 w-full rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] disabled:cursor-not-allowed disabled:opacity-50', className)} id={id} ref={ref} {...props} />;
});

export type FormDescriptionProps = Omit<HTMLAttributes<HTMLParagraphElement>, 'id'>;

export const FormDescription = forwardRef<HTMLParagraphElement, FormDescriptionProps>(function FormDescription(
  { className, ...props },
  ref,
) {
  const { descriptionId, setHasDescription } = useFormField();

  useEffect(() => {
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [setHasDescription]);

  return <p className={cn('text-sm text-[var(--foreground-muted)]', className)} ref={ref} {...props} id={descriptionId} />;
});

export type FormMessageProps = Omit<HTMLAttributes<HTMLParagraphElement>, 'id'>;

export const FormMessage = forwardRef<HTMLParagraphElement, FormMessageProps>(function FormMessage(
  { className, ...props },
  ref,
) {
  const { errorId, invalid, setHasMessage } = useFormField();

  useEffect(() => {
    setHasMessage(Boolean(props.children));
    return () => setHasMessage(false);
  }, [props.children, setHasMessage]);

  if (!invalid || !props.children) {
    return null;
  }
  return <p className={cn('border-s-2 border-[var(--destructive)] bg-[var(--destructive-light)] px-2 py-1 text-sm text-[var(--foreground)]', className)} ref={ref} {...props} id={errorId} />;
});
